import { Avatar, CircularProgress } from '@mui/material'
import ErrorFallback from 'components/ErrorFallback'
import { usePharmacyMedicationDetails } from 'data/treatments'
import { useParams } from 'react-router-dom'
import { formatCurrency, formatDate } from 'utils'
import { usePharmacy } from 'data/pharmacy'
import { useDosageForms } from 'data/product'

export default function PharmacyMedicationDetails() {
  const { medicationId, id } = useParams()
  const { data, isLoading, isError, refetch } = usePharmacyMedicationDetails(medicationId as string)
  const { data: pharm } = usePharmacy(id as string)
  const { data: dosageForms } = useDosageForms()
  const measurement = dosageForms?.find(
    (d) =>
      d.measurement_unit === data?.dosage?.measurement ||
      d.possible_dosage_units.includes(data?.dosage?.measurement || '')
  )

  return isLoading ? (
    <div className="h-96 w-full grid place-items-center">
      <CircularProgress />
    </div>
  ) : isError ? (
    <ErrorFallback
      message="Something went wrong"
      description="Sorry , we could not fetch medication"
      reset={refetch}
    />
  ) : (
    <div className="space-y-8 lg:w-[39rem] mx-auto grid gap-6">
      {pharm && (
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="grid place-items-center gap-2">
            <Avatar
              className="size-12 bg-brand-700 capitalize shadow-lg"
              alt={pharm?.pharmacy_name}
              src={pharm?.logo_url || pharm?.pharmacy_name}
            />
            <p className="font-bold">{pharm.pharmacy_name} Pharmacy</p>
          </div>

          <p>Date - {formatDate(data.created_at)}</p>
        </div>
      )}
      <div className="gap-6 flex flex-col items-center">
        <p className="font-bold text-brand-900">Medication</p>

        <div className="rounded-xl grid md:grid-cols-2 p-5 border border-default font-medium gap-4 w-full mb-3">
          <p className="font-medium ">
            <span className="font-semibold">Product</span> : {data.metadata?.drug_name}
          </p>

          <p className="font-medium">
            <span className="font-semibold">Quantity</span> : {data.dosage.quantity}
          </p>

          <p className="font-medium">
            <span className="font-semibold">Brand</span> : {data.metadata.brand}
          </p>

          <p className="font-medium ">
            <span className="font-semibold">Dose</span> : {data.dose}
          </p>

          <p className="font-medium ">
            <span className="font-semibold">Dosage form</span> : {data.dosage_form}
          </p>
          <div className="justify-between flex font-medium">
            <p>
              {' '}
              <span className="font-semibold">Price</span> : {formatCurrency(data.price)}
            </p>
          </div>
        </div>
        <h1 className="text-center text-brand-900 font-bold text-lg">Dosage Instruction</h1>

        <div className="rounded-xl p-5 border border-default font-medium space-y-4 w-full mb-3">
          <InstructionItem
            title="Dosage instruction"
            content={
              <div className="bg-[#F8F8F8] rounded-[4.23px] px-3 py-2.5">
                <p className="text-sm text-neutral-500">
                  {measurement?.command} {data?.dosage?.quantity}{' '}
                  {measurement?.measurement_unit || 'Take'} Every {data?.dosage?.interval}{' '}
                  {data?.dosage?.frequency} For {data?.dosage?.duration}{' '}
                  {data?.dosage?.duration_frequency}
                </p>
              </div>
            }
          />
          <InstructionItem title="Route" content={data.dosage.route} />
          <InstructionItem title="Note" content={data.dosage.notes} />
        </div>
      </div>
    </div>
  )
}

function InstructionItem(props: { title: string; content: JSX.Element | string }) {
  return (
    <div className="grid grid-cols-12 items-center">
      <p className="col-span-4 font-semibold">{props.title} :</p>
      <p className="col-span-8">{props.content}</p>
    </div>
  )
}
